import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    Typography,
    Switch,
    FormControlLabel,
    Button,
    Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

import { apiGetCommunityCreators } from "../../../services/apiGetCommunityCreators";
import { apiGetPosts } from "../../../services/apiGetPosts";
import { apiGetLogs } from "../../../services";
import { useCommunityId } from "../../../hooks/useCommunityId";

import { Loader } from "../../../components/ui/Loader";
import { CommunityCreatorDetailsModal } from "../../../components/ui/CommunityCreatorDetailsModal";

import { returnEnvironment } from "../../../utils";

export const MembersReport = () => {
    const [loading, setLoading] = useState(false);
    const [creatorStats, setCreatorStats] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [isUpdated, setIsUpdated] = useState(false);
    const [showOnlyNoAccess, setShowOnlyNoAccess] = useState(false);
    const [showOnlyWithPosts, setShowOnlyWithPosts] = useState(false);
    const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
    const [selectedCreatorId, setSelectedCreatorId] = useState(null);

    const communityId = useCommunityId();

    const showCreatorModal = (creatorId) => {
        setSelectedCreatorId(creatorId);
        setIsCreatorModalOpen(true);
    };

    const hideCreatorModal = () => {
        setIsCreatorModalOpen(false);
        setSelectedCreatorId(null);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            setIsUpdated(false);

            const [postsResponse, creatorsResponse] = await Promise.all([
                apiGetPosts("ALL", communityId),
                apiGetCommunityCreators(communityId, ""),
            ]);

            const posts = Array.isArray(postsResponse) ? postsResponse : [];
            const creatorsData = Array.isArray(creatorsResponse) ? creatorsResponse : [];

            const filteredCreators =
                returnEnvironment() === "wizard"
                    ? creatorsData.filter((creator) => creator.cliente === 7)
                    : creatorsData;

            const logsResponse = await apiGetLogs({
                idCreators: filteredCreators.map((creator) => creator.id),
            });
            const logs = Array.isArray(logsResponse) ? logsResponse : [];

            const stats = filteredCreators.map((creator) => {
                const creatorPosts = posts.filter((post) => post?.creator?.id === creator.id && post?.idVideo !== null);
                const totalLikes = creatorPosts.reduce((sum, post) => {
                    const postLikes = post?.engajamento?.likes || [];
                    return sum + postLikes.length;
                }, 0);
                const totalComments = creatorPosts.reduce((sum, post) => {
                    const postComments = post?.engajamento?.comentarios || [];
                    return sum + postComments.length;
                }, 0);

                const userLogs = logs.filter((log) => log.idCreator === creator.id || log.idUsuario === creator.id);

                const lastLog =
                    userLogs.length > 0 ? new Date(Math.max(...userLogs.map((log) => new Date(log.datetime)))) : null;

                return {
                    id: creator.id,
                    nome: creator.nome,
                    email: creator.email,
                    totalVideos: creatorPosts.length,
                    totalLikes: totalLikes,
                    totalComments: totalComments,
                    franchise: creator?.franchise?.name ? creator.franchise.name : "Sem dados",
                    ultimoAcesso: lastLog ? lastLog.toLocaleString() : "Sem acesso",
                };
            });

            setCreatorStats(stats);
            setIsUpdated(true);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            setCreatorStats([]);
            setIsUpdated(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [communityId]);

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const sortedStats = React.useMemo(() => {
        if (!creatorStats || !Array.isArray(creatorStats)) return [];

        let filteredItems = [...creatorStats];

        if (showOnlyNoAccess) {
            filteredItems = filteredItems.filter((creator) => creator.ultimoAcesso === "Sem acesso");
        }

        if (showOnlyWithPosts) {
            filteredItems = filteredItems.filter((creator) => creator.totalVideos > 0);
        }

        let sortedItems = [...filteredItems];
        if (sortConfig.key) {
            sortedItems.sort((a, b) => {
                if (!a || !b) return 0;

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (valA < valB) {
                    return sortConfig.direction === "asc" ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortedItems;
    }, [creatorStats, sortConfig, showOnlyNoAccess, showOnlyWithPosts]);

    if (!isUpdated) {
        return <Loader />;
    }

    return (
        <Box>
            <Card sx={{ p: 3, mb: 3, mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6">Relatório de Membros</Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyNoAccess}
                                    onChange={(e) => setShowOnlyNoAccess(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Filtrar quem nunca acessou"
                            sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyWithPosts}
                                    onChange={(e) => setShowOnlyWithPosts(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Apenas com posts"
                            sx={{ mr: 2 }}
                        />
                        <Tooltip title="Exportar para Excel">
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => exportToExcel(sortedStats)}
                                sx={{
                                    color: "var(--primary-color)",
                                    borderColor: "var(--primary-color)",
                                    "&:hover": {
                                        borderColor: "var(--primary-color-dark)",
                                        backgroundColor: "rgba(var(--primary-color-rgb), 0.04)",
                                        transform: "scale(1.02)",
                                        transition: "all 0.2s",
                                    },
                                }}
                            >
                                Exportar
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
                <TableContainer component={Paper} sx={{ boxShadow: "none", overflow: "auto" }}>
                    <Table sx={{ minWidth: 650 }}>
                        <colgroup>
                            <col style={{ width: { xs: "20%", sm: "15%" } }} />
                            <col style={{ width: { xs: "20%", sm: "25%" } }} />
                            <col style={{ width: { xs: "15%", sm: "15%" } }} />
                            <col style={{ width: { xs: "15%", sm: "15%" } }} />
                            <col style={{ width: { xs: "15%", sm: "15%" } }} />
                            <col style={{ width: { xs: "15%", sm: "15%" } }} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => handleSort("nome")} style={{ cursor: "pointer" }}>
                                    Nome
                                </TableCell>
                                <TableCell onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                                    Email
                                </TableCell>
                                <TableCell onClick={() => handleSort("unidade")} style={{ cursor: "pointer" }}>
                                    Unidade
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalVideos")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Vídeos
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalLikes")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Curtidas Recebidas
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalComments")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Comentários Recebidos
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("ultimoAcesso")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Último Acesso
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedStats.map((creator) => (
                                <TableRow
                                    key={creator.id}
                                    onClick={() => showCreatorModal(creator.id)}
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: "0.875rem", sm: "1rem" },
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: "150px",
                                            maxWidth: "200px",
                                        }}
                                    >
                                        {creator.nome}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: "0.875rem", sm: "1rem" },
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: "200px",
                                            maxWidth: "300px",
                                        }}
                                    >
                                        {creator.email}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                                    >
                                        {creator.franchise}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {creator.totalVideos}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {creator.totalLikes}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {creator.totalComments}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                            color: creator.ultimoAcesso === "Sem acesso" ? "error.main" : "inherit",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {creator.ultimoAcesso}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedStats.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        {loading ? "Carregando..." : "Nenhum membro encontrado"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <CommunityCreatorDetailsModal
                isOpen={isCreatorModalOpen}
                onClose={hideCreatorModal}
                creatorId={selectedCreatorId}
            />
        </Box>
    );
};

const exportToExcel = (sortedStats) => {
    const headers = [
        "Nome",
        "Email",
        "Vídeos",
        "Curtidas Recebidas",
        "Comentários Recebidos",
        "Último Acesso",
    ];

    const data = sortedStats.map((creator) => [
        creator.nome,
        creator.email,
        creator.totalVideos,
        creator.totalLikes,
        creator.totalComments,
        creator.ultimoAcesso,
    ]);

    const worksheetData = [headers, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Membros");
    
    XLSX.writeFile(
        workbook,
        `membros_relatorio_${new Date().toISOString().split("T")[0]}.xlsx`
    );
};
